<template>
  <v-container class="d-flex justify-center align-center fill-height">
    <v-card class="pa-8 profile-card" max-width="600">
      <v-card-title class="text-h4">User Profile</v-card-title>
      <v-divider class="mb-6"></v-divider>
      <v-form>
        <v-text-field
            v-model="displayName"
            label="Display Name"
            outlined
        ></v-text-field>
        <v-dialog v-model="passwordDialog">
          <template v-slot:activator="{ on }">
            <div class="text-center">
              <v-btn text class="change-password-btn" v-on="on">
                Update Password
              </v-btn>
            </div>
          </template>
          <v-card>
            <v-card-title class="zapa-fill mb-5">Change Password</v-card-title>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                      id="password"
                      v-model="pushPw"
                      label="* Enter New Password"
                      name="password"
                      :append-icon="showPassword ? 'far fa-eye' : 'far fa-eye-slash'"
                      @click:append="toggleShow"
                      :type="showPassword ? 'text' : 'password'"
                      autocomplete="new-password"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field type="password" v-model="confirmPw"
                                label="* Re-enter New Password"></v-text-field>
                  <Transition name="slide-fade">
                    <v-pass-validator :value="pushPw" v-if="pushPw && pushPw.length > 0"></v-pass-validator>
                  </Transition>
                </v-col>
              </v-row>
              <v-expand-transition v-if="confirmPw.length > 0 && confirmPw.length < 8">
                <v-row>
                  <span style="color: red">Please enter a password with at least 8 characters.</span>
                </v-row>
              </v-expand-transition>
              <v-expand-transition v-if="pushPw !== confirmPw">
                <v-row>
                  <span style="color: red">Password confirmation does not match.</span>
                </v-row>
              </v-expand-transition>
            </v-container>

            <v-card-actions>
              <v-btn text :disabled="!confirmPass || !pushPw" @click="changePassword">Save</v-btn>
              <v-btn text @click="cancelDialog()">Cancel</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <div class="profile-info">
          <label>Email Address</label>
          <p>{{ cognito && cognito.attributes ? cognito.attributes.email : '' }}</p>
        </div>

        <div class="profile-info">
          <label>Account Type</label>
          <p>{{ cognito && cognito.attributes ? cognito.attributes['custom:mutableUserType'] : '' }}</p>
        </div>

        <!-- Business Name (Non-editable Field) -->
        <div class="profile-info">
          <label>Business Name</label>
          <p>{{ organization && organization.companyName ? organization.companyName : (cognito && cognito.attributes ? cognito.attributes['custom:orgName'] : '') }}</p>
        </div>
        
        <!-- Save Button -->
        <v-btn color="primary" class="mt-6" block @click="updateDisplayName()">Save</v-btn>
      </v-form>
      
      <!-- MFA Security Section -->
      <v-divider class="my-8"></v-divider>
      <v-card-title class="text-h5 px-0">Security</v-card-title>
      
      <div class="mfa-section">
        <div class="d-flex align-center justify-space-between mb-4">
          <div>
            <h3 class="text-h6 mb-1">Multi-Factor Authentication</h3>
            <p class="text-caption mb-0 grey--text">
              Add an extra layer of security to your account
            </p>
          </div>
          <v-chip :color="mfaEnabled ? 'success' : 'grey'" small>
            {{ mfaEnabled ? (mfaMethodLabel ? mfaMethodLabel : 'Enabled') : 'Disabled' }}
          </v-chip>
        </div>
        
        <!-- MFA Required by Organization Alert -->
        <v-alert v-if="mfaRequiredByOrg && !mfaEnabled" type="warning" dense text class="mb-4">
          <div class="d-flex align-center justify-space-between">
            <div>
              Your organization requires MFA. 
              <span v-if="mfaDaysRemaining > 0">
                You have <strong>{{ mfaDaysRemaining }} day{{ mfaDaysRemaining !== 1 ? 's' : '' }}</strong> remaining to complete setup.
              </span>
              <span v-else class="red--text font-weight-bold">
                Setup required now!
              </span>
            </div>
          </div>
        </v-alert>
        
        <!-- MFA Status Display -->
        <div v-if="mfaEnabled" class="mb-4">
          <v-list dense>
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="success">{{ mfaMethodIcon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  <span v-if="mfaMethodLabel">Current method: <strong>{{ mfaMethodLabel }}</strong></span>
                  <span v-else>MFA is active on your account</span>
                </v-list-item-title>
                <v-list-item-subtitle v-if="mfaRequiredByOrg">
                  Required by your organization
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else>
                  Voluntarily enabled for extra security
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="!mfaRequiredByOrg">
                <v-btn text color="error" small @click="confirmRemoveMfa = true">
                  Remove
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <p v-if="mfaRequiredByOrg" class="text-caption grey--text mb-0">
            Your organization requires MFA, so it can't be fully removed - but you can switch methods
            (e.g. Text to Authenticator App) below. Your account stays protected during the change.
          </p>
        </div>
        
        <!-- Setup / Change MFA Method Button -->
        <!-- Always available when the setup panel isn't already open, so users can switch
             methods even when MFA is required (switching never disables MFA). -->
        <v-btn 
          v-if="!showMfaSetup"
          color="primary"
          outlined
          block
          @click="showMfaSetup = true"
          class="mb-4"
        >
          <v-icon left>mdi-shield-plus</v-icon>
          {{ mfaEnabled ? 'Change MFA Method' : 'Set Up MFA' }}
        </v-btn>
        
        <!-- Inline MFA Setup -->
        <v-expand-transition>
          <div v-if="showMfaSetup">
            <v-card outlined class="pa-4">
              <MfaSetup 
                :user="user"
                @mfa-setup-complete="onMfaSetupComplete"
                @close="showMfaSetup = false"
              />
            </v-card>
          </div>
        </v-expand-transition>
      </div>
      
      <!-- Confirm Remove MFA Dialog -->
      <v-dialog v-model="confirmRemoveMfa" max-width="500">
        <v-card>
          <v-card-title class="headline">Remove Multi-Factor Authentication?</v-card-title>
          <v-card-text>
            <p>Are you sure you want to remove MFA from your account? This will make your account less secure.</p>
            <v-alert type="info" dense text class="mt-4">
              You can re-enable MFA at any time from your profile settings.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="confirmRemoveMfa = false">Cancel</v-btn>
            <v-btn color="error" text @click="removeMfa" :loading="removingMfa">Remove MFA</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-container>
</template>


<script>
  import VPassValidator from '../components/VPassValidator';
  import MfaSetup from '../components/MfaSetup';
	import {VueController} from '../service/support/vuex-controller';
	import userService from "../service/user-service";
	import orgService from "../service/org-service";
	import Auth from "@aws-amplify/auth";

	export default VueController("About", {
		components: {
      VPassValidator,
      MfaSetup
    },
    state: [
			"cognito",
        "organization",
        "user"
		],
		data() {
			return {
				blogs: [],
        passwordDialog: false,
        displayName: "",
        showPassword: false,
        pushPw: "",
        confirmPw: "",
        saveDisplayNameInProgress: false,
        showMfaSetup: false,
        confirmRemoveMfa: false,
        removingMfa: false,
        mfaMethod: ""
			}
		},
    computed: {
      confirmPass() {
        return this.pushPw === this.confirmPw;
      },
      mfaEnabled() {
        return this.user && this.user.mfaSetupCompleted === true;
      },
      mfaRequiredByOrg() {
        // The org flag is authoritative and persists after setup. mfaEnforcementDate is
        // only a pre-setup grace-period signal and gets cleared once MFA is completed,
        // so relying on it alone would wrongly show "Remove" to org-required users.
        const orgRequires = this.organization && this.organization.requireMfaForVaultAccess === true;
        const hasEnforcementDate = this.user && this.user.mfaEnforcementDate !== null && this.user.mfaEnforcementDate !== undefined;
        return orgRequires || hasEnforcementDate;
      },
      mfaDaysRemaining() {
        if (!this.user || !this.user.mfaEnforcementDate) return 0;
        const now = new Date();
        const enforcementDate = new Date(this.user.mfaEnforcementDate);
        const diff = enforcementDate - now;
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
        return days > 0 ? days : 0;
      },
      mfaMethodLabel() {
        if (this.mfaMethod === "SOFTWARE_TOKEN_MFA" || this.mfaMethod === "TOTP") return "Authenticator App";
        if (this.mfaMethod === "SMS_MFA" || this.mfaMethod === "SMS") return "Text message (SMS)";
        return "";
      },
      mfaMethodIcon() {
        if (this.mfaMethod === "SOFTWARE_TOKEN_MFA" || this.mfaMethod === "TOTP") return "mdi-cellphone-key";
        if (this.mfaMethod === "SMS_MFA" || this.mfaMethod === "SMS") return "mdi-message-text";
        return "mdi-shield-check";
      }
    },
    mounted() {
      if (this.cognito && this.cognito.attributes) {
        this.displayName = this.cognito.attributes.name + "";
      }
      this.loadMfaMethod();
    },
		methods: {
			grabFilterUser() {
				alert(JSON.stringify(this.cognito));
			},
      toggleShow() {
        this.showPassword = !this.showPassword;
      },
      cancelDialog() {
        this.passwordDialog = false;
        this.pushPw = "";
        this.confirmPw = "";
      },
      async changePassword() {
        try{
          await userService.setNewPassword(this.cognito,this.pushPw);
          this.notification("success", "Password changed successfully.");
          this.pushPw = "";
          this.confirmPw = "";
          this.passwordDialog = false;
        }catch (e){
          this.notification("error", "Unable to change password, please try again.");
        }

      },
      async updateDisplayName() {
        let self = this;
        self.saveDisplayNameInProgress = true;
        try {
          await userService.updateUserDisplayName(this.cognito, self.displayName);

          self.saveDisplayNameInProgress = false;
        } catch (error) {
          self.notification("error", "Unable to save user display name, please try again.");
          self.saveDisplayNameInProgress = false;
        }
        console.log("Name updated");
      },
      onMfaSetupComplete(updatedUser) {
        this.showMfaSetup = false;
        this.notification("success", "MFA has been successfully enabled for your account.");
        // Refresh user data to get updated MFA status
        this.refreshUserData();
        // Reload the active method so the profile reflects the switch/setup.
        this.loadMfaMethod();
      },
      async loadMfaMethod() {
        try {
          const user = await Auth.currentAuthenticatedUser();
          // bypassCache so we reflect the latest preference right after a change.
          const pref = await Auth.getPreferredMFA(user, { bypassCache: true });
          this.mfaMethod = pref ? pref : "NOMFA";
        } catch (error) {
          console.error("Error loading MFA method:", error);
        }
      },
      async refreshUserData() {
        try {
          const userData = await orgService.query("getUserById");
          this.$store.state.user = userData;
        } catch (error) {
          console.error("Error refreshing user data:", error);
        }
      },
      async removeMfa() {
        this.removingMfa = true;
        try {
          // Get current session
          const user = await Auth.currentAuthenticatedUser();
          
          // Disable TOTP MFA
          await Auth.setPreferredMFA(user, 'NOMFA');
          
          // Update user record in DynamoDB to mark MFA as not completed
          await orgService.mutation("updateUserMfaStatus", {
            mfaSetupCompleted: false
          });
          
          this.notification("success", "MFA has been removed from your account.");
          this.confirmRemoveMfa = false;
          
          // Refresh user data
          await this.refreshUserData();
          await this.loadMfaMethod();
        } catch (error) {
          console.error("Error removing MFA:", error);
          this.notification("error", "Failed to remove MFA. Please try again.");
        } finally {
          this.removingMfa = false;
        }
      }
		}
	});
</script>

<style scoped lang="scss">

.profile-card {
  max-width: 600px;
  min-width: 400px;
  border-radius: 8px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
}


.profile-info {
  margin-bottom: 20px;
}

.profile-info label {
  font-size: 18px;
  color: #757575;
  font-weight: 600;
  display: block;
  margin-bottom: 5px;
}

.profile-info p {
  font-size: 16px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 5px;
  color: #333;
  margin: 0;
}

.change-password-btn {
  text-transform: none;
  font-size: 14px;
  text-align: center;
  color: #0D7542;
  padding: 0;
  margin-bottom: 20px;
  cursor: pointer;
}

.save-btn {
  font-size: 14px;
  font-weight: bold;
  width: auto;
  float: right;
  padding: 8px 16px;
}

.v-card-title {
  text-align: center;
  color: #3a3a3a;
}

.v-btn {
  font-size: 16px;
  font-weight: bold;
}

.fill-height {
  min-height: 100vh;
}

.v-dialog__content {
  max-width: 600px;
  margin: auto calc(50% - 300px);
}

.v-alert__content {
  word-break: break-word;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.8s ease-out;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

.mfa-section {
  margin-top: 16px;
}

.mfa-section h3 {
  font-weight: 500;
  color: #2c3e50;
}
</style>
